import React, { FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import styles from "../passwordReset.module.css";

const AUTH_API_URL =
  process.env.REACT_APP_AUTH_ENDPOINT || "http://localhost:8080";

type DialogState = {
  title: string;
  message: string;
  success: boolean;
} | null;

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [dialog, setDialog] = useState<DialogState>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${AUTH_API_URL}/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setDialog({
          title: "Check your email",
          message:
            "If an account exists for that email, we've sent a password reset link.",
          success: true,
        });
      } else {
        setDialog({
          title: "Request failed",
          message:
            data?.detail ||
            "We couldn't request a password reset. Please try again.",
          success: false,
        });
      }
    } catch {
      setDialog({
        title: "Request failed",
        message:
          "We couldn't reach the password reset service. Check your connection and try again.",
        success: false,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <main className={styles.card}>
        <Link to="/" className={styles.brand}>
          DriveNow
        </Link>
        <div className={styles.tagline}>Peer-to-peer car marketplace</div>
        <h1 className={styles.title}>Forgot your password?</h1>
        <p className={styles.body}>
          Confirm the email address on your account and we'll send you a secure
          password reset link.
        </p>

        <form onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label htmlFor="forgot-password-email">Email address</label>
            <input
              id="forgot-password-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              disabled={submitting}
            />
          </div>
          <button
            type="submit"
            className={styles.primaryButton}
            disabled={submitting}
          >
            {submitting ? "Sending…" : "Send reset link"}
          </button>
        </form>
        <Link to="/login" className={styles.secondaryButton}>
          Back to sign in
        </Link>
      </main>

      <Dialog
        open={dialog !== null}
        onClose={() => setDialog(null)}
        aria-labelledby="forgot-password-dialog-title"
      >
        <DialogTitle id="forgot-password-dialog-title">
          {dialog?.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{dialog?.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          {dialog?.success ? (
            <Button component={Link} to="/login">
              Return to sign in
            </Button>
          ) : (
            <Button onClick={() => setDialog(null)} autoFocus>
              Try again
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ForgotPasswordPage;
